import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";

const setupControlsVisibility = (swiper, controls = []) => {
    if (!controls.length) {
        return;
    }

    const update = () => {
        const hideControls = swiper.params.watchOverflow && swiper.isLocked;

        controls.forEach((control) => {
            if (!(control instanceof HTMLElement)) {
                return;
            }

            control.classList.toggle("hidden", hideControls);
            control.setAttribute("aria-hidden", hideControls ? "true" : "false");

            if (control instanceof HTMLButtonElement || control instanceof HTMLAnchorElement) {
                control.tabIndex = hideControls ? -1 : 0;
            }
        });
    };

    swiper.on("lock", update);
    swiper.on("unlock", update);
    swiper.on("resize", update);
    swiper.on("slidesLengthChange", update);
    swiper.on("breakpoint", update);

    update();
};

const debounce = (fn, wait = 100) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(null, args), wait);
    };
};

const equalizeSliderHeights = (root, swiperInstance) => {
    if (!(root instanceof HTMLElement)) {
        return;
    }

    const wrappers = root.querySelectorAll(".swiper-wrapper");
    if (!wrappers.length) {
        return;
    }

    const images = root.querySelectorAll("img");

    const setEqualHeights = () => {
        wrappers.forEach((wrapper) => {
            const slides = wrapper.querySelectorAll(":scope > .swiper-slide");
            if (!slides.length) {
                return;
            }

            slides.forEach((slide) => {
                slide.style.height = "auto";
            });

            let max = 0;
            slides.forEach((slide) => {
                max = Math.max(max, slide.offsetHeight);
            });

            slides.forEach((slide) => {
                slide.style.height = `${max}px`;
            });
        });
    };

    const debounced = debounce(setEqualHeights, 100);
    swiperInstance?.on("resize", debounced);
    swiperInstance?.on("slideChange", debounced);
    window.addEventListener("resize", debounced);

    images.forEach((img) => {
        if (img.complete) {
            return;
        }
        img.addEventListener("load", debounced, { once: true });
        img.addEventListener("error", debounced, { once: true });
    });

    setEqualHeights();
};

document.addEventListener("DOMContentLoaded", () => {
    const sliders = document.querySelectorAll(".slider-products, .slider-cards");
    const slidersSingle = document.querySelectorAll(".slider-text-media, .slider-testimonials, .slider-advantages, .slider-text-builder");
    const slidersImages = document.querySelectorAll(".slider-images");

    slidersSingle.forEach((el) => {
        const swiperEL = el.querySelector(".swiper");
        const nextEl = el.querySelector(".swiper-button-next");
        const prevEl = el.querySelector(".swiper-button-prev");
        const paginationEl = el.querySelector(".pagination");

        const modules = [Navigation];
        if (paginationEl) {
            modules.push(Pagination);
        }

        const swiper = new Swiper(swiperEL, {
            modules,
            speed: 400,
            spaceBetween: 12,
            slidesPerView: 1,
            watchOverflow: true,
            navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
            pagination: paginationEl ? { el: paginationEl, clickable: true } : undefined,
        });

        setupControlsVisibility(swiper, [prevEl, nextEl, paginationEl]);

        if (el.classList.contains("slider-testimonials")) {
            equalizeSliderHeights(swiperEL ?? el, swiper);
        }
    });

    slidersImages.forEach((el) => {
        const swiperEL = el.querySelector(".swiper");
        const nextEl = el.querySelector(".swiper-button-next");
        const prevEl = el.querySelector(".swiper-button-prev");

        const swiper = new Swiper(swiperEL, {
            modules: [Navigation],
            speed: 400,
            spaceBetween: 12,
            slidesPerView: 1,
            watchOverflow: true,
            navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
            breakpoints: {
                480: { 
                    slidesPerView: 1.5, 
                    spaceBetween: 16 
                },
                1440: {
                    slidesPerView: 2.25,
                }
            },
        });

        setupControlsVisibility(swiper, [prevEl, nextEl]);
    });

    sliders.forEach((el) => {
        const scope = el.closest(".container") || el.parentElement || document;
        const nextEl = scope.querySelector(".swiper-button-next");
        const prevEl = scope.querySelector(".swiper-button-prev");

        const swiper = new Swiper(el, {
            modules: [Navigation],
            speed: 400,
            spaceBetween: 12,
            slidesPerView: 1,
            watchOverflow: true,
            navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
            breakpoints: {
                768: { slidesPerView: 2, spaceBetween: 16 },
                1025: { slidesPerView: 3, spaceBetween: 20 },
                // 1600: { slidesPerView: 4, spaceBetween: 24 },
            },
        });

        setupControlsVisibility(swiper, [prevEl, nextEl]);

        equalizeSliderHeights(el, swiper);
    });

    const timelines = document.querySelectorAll("[data-timeline]");

    timelines.forEach((timeline) => {
        const swiperEl = timeline.querySelector("[data-timeline-swiper]");
        const slides = swiperEl ? Array.from(swiperEl.querySelectorAll(".timeline-item")) : [];
        const titleTarget = timeline.querySelector("[data-timeline-title]");
        const textTarget = timeline.querySelector("[data-timeline-text]");
        const iconTarget = timeline.querySelector("[data-timeline-icon]");
        const prevEl = timeline.querySelector("[data-timeline-prev]");
        const nextEl = timeline.querySelector("[data-timeline-next]");

        if (!swiperEl || !slides.length || !titleTarget || !textTarget) {
            return;
        }

        const timelineSwiper = new Swiper(swiperEl, {
            speed: 400,
            slidesPerView: 1.5,
            spaceBetween: 16,
            watchOverflow: true,
            breakpoints: {
                640: { slidesPerView: 2.5, spaceBetween: 24 },
                1024: { slidesPerView: 4.5, spaceBetween: 32 },
            },
        });

        setupControlsVisibility(timelineSwiper, [prevEl, nextEl]);

        slides.forEach((slide, index) => {
            slide.dataset.timelineIndex = `${index}`;
        });

        const clampIndex = (index) => Math.min(Math.max(index, 0), slides.length - 1);

        let activeIndex = 0;
        let suppressNextSlideChange = false;

        const updateControlsState = () => {
            if (prevEl instanceof HTMLButtonElement) {
                prevEl.toggleAttribute("disabled", activeIndex <= 0);
            }

            if (nextEl instanceof HTMLButtonElement) {
                nextEl.toggleAttribute("disabled", activeIndex >= slides.length - 1);
            }
        };

        const ensureSlideVisible = (index) => {
            const slide = slides[index];
            if (!slide) {
                return;
            }

            const swiperSlides = Array.from(timelineSwiper.slides || []);
            const swiperIndex = swiperSlides.indexOf(slide);

            if (swiperIndex === -1) {
                return;
            }

            if (timelineSwiper.activeIndex === swiperIndex) {
                return;
            }

            suppressNextSlideChange = true;
            timelineSwiper.slideTo(swiperIndex);
        };

        const updateIcon = (slide) => {
            if (!(iconTarget instanceof HTMLElement)) {
                return;
            }

            iconTarget.textContent = "";

            const iconUrl = slide?.dataset?.icon;
            if (!iconUrl) {
                return;
            }

            const image = document.createElement("img");
            image.src = iconUrl;
            image.alt = slide?.dataset?.title ? `${slide.dataset.title} icon` : "";
            image.loading = "lazy";
            image.decoding = "async";

            iconTarget.appendChild(image);
        };

        const setActive = (index, { skipEnsureVisible = false } = {}) => {
            const safeIndex = clampIndex(index);
            const activeSlide = slides[safeIndex];

            activeIndex = safeIndex;

            slides.forEach((slide, slideIndex) => {
                slide.classList.toggle("timeline-item--active", slideIndex === safeIndex);
            });

            if (activeSlide) {
                titleTarget.textContent = activeSlide.dataset.title ?? "";
                textTarget.textContent = activeSlide.dataset.text ?? "";
                updateIcon(activeSlide);
            }

            updateControlsState();

            if (!skipEnsureVisible) {
                ensureSlideVisible(safeIndex);
            }
        };

        setActive(timelineSwiper.realIndex, { skipEnsureVisible: true });

        timelineSwiper.on("slideChange", () => {
            if (suppressNextSlideChange) {
                suppressNextSlideChange = false;
                return;
            }
            const currentActiveSlide = slides[activeIndex];
            if (currentActiveSlide?.classList.contains("swiper-slide-visible")) {
                return;
            }

            const visibleSlides = Array.from(timelineSwiper.slides || []).filter((slide) =>
                slide.classList.contains("swiper-slide-visible")
            );
            const fallbackSlide = visibleSlides[Math.floor(visibleSlides.length / 2)] ?? visibleSlides[0];

            const indexAttr = fallbackSlide?.dataset?.timelineIndex;
            const parsedIndex = indexAttr ? Number(indexAttr) : Number.NaN;

            if (!Number.isNaN(parsedIndex)) {
                setActive(parsedIndex, { skipEnsureVisible: true });
            }
        });

        slides.forEach((slide, index) => {
            const button = slide.querySelector("button");
            if (!(button instanceof HTMLButtonElement)) {
                return;
            }

            button.addEventListener("click", () => {
                setActive(index);
            });
        });

        if (prevEl instanceof HTMLButtonElement) {
            prevEl.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (activeIndex <= 0) {
                    return;
                }

                setActive(activeIndex - 1);
            });
        }

        if (nextEl instanceof HTMLButtonElement) {
            nextEl.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (activeIndex >= slides.length - 1) {
                    return;
                }

                setActive(activeIndex + 1);
            });
        }
    });
});

// const swiper = new Swiper(...);
